import React, { useMemo } from 'react';

import { isDocumentElement, isTableCellElement } from '@aline/common';
import { Typography } from '@mui/material';
import { Editor } from 'slate';
import { SelectButton } from 'src/components';
import { PageBreakIcon } from 'src/components/icons/PageBreakIcon';
import {
  useEditorTabStateSelector,
  useGetSelBoundaryEntry,
} from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading, useSelection } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  buttonText: {
    paddingLeft: theme.spacing(0.5),
  },
}));

export function PageBreakButton(): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();

  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const currentUserId = useCurrentUserId();
  const disabled = useIsPageBreakDisabled();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  function insertPageBreak(): void {
    if (!editingMode || !currentUserId) return;

    AuthorizedTransforms.insertPageBreak(editor, { authorId: currentUserId, editingMode });
    ClientTransforms.focus(editor);
  }

  return (
    <SelectButton disabled={disabled} onClick={insertPageBreak}>
      <PageBreakIcon />
      <Typography className={classes.buttonText} color="inherit" variant="caption">
        Page break
      </Typography>
    </SelectButton>
  );
}

function useIsPageBreakDisabled() {
  const editor = useEditor();
  const editorLoading = useEditorLoading();
  const selection = useSelection();
  const selBoundaryEntry = useGetSelBoundaryEntry();

  return useMemo(() => {
    if (!selection || editorLoading || !selBoundaryEntry) return true;

    const tableCellEntry = Editor.above(editor, { match: isTableCellElement });
    const [selBoundaryNode] = selBoundaryEntry;

    return !!tableCellEntry || !isDocumentElement(selBoundaryNode);
  }, [editor, editorLoading, selBoundaryEntry, selection]);
}
